/**
 * Simulated /api/drive-time — the windshield-time row on the Customer
 * Information card and the Work Day Planner's round-trip header.
 *
 * Shape is `DriveTimeResponse` from src/lib/api/driveTime.ts. Both the GET
 * (office → loss address) and the POST (office → stops → office) are answered.
 *
 * Generated, deterministically: miles come from a hash of the normalised
 * address, so the same address always returns the same distance, and minutes
 * follow from it at a plausible suburban-north-Jersey average speed with a
 * fixed pull-out allowance. `found: true` — the demo never shows an
 * unresolvable address.
 */
import { seededFloat } from './prng';
import { round2 } from './finance';

const ORIGIN_LABEL = 'PDQ Headquarters';
/** Average door-to-door speed, mph, plus a fixed allowance for getting out of the yard. */
const AVG_MPH = 31;
const PULL_OUT_MINUTES = 4;

export interface AddressLike {
  address?: string | null; city?: string | null; state?: string | null; zip?: string | null;
}

export function addressKey(a: AddressLike): string {
  return [a.address, a.city, a.state, a.zip]
    .map((v) => (v ?? '').trim()).filter(Boolean).join(', ')
    .toLowerCase().replace(/\s+/g, ' ').trim();
}

function legMiles(key: string): number {
  if (!key) return 0;
  // Most work is inside the service area; a tail runs further out.
  const base = seededFloat(`${key}|miles`, 2.4, 26);
  const tail = seededFloat(`${key}|tail`, 0, 1) > 0.88 ? seededFloat(`${key}|far`, 8, 22) : 0;
  return round2(base + tail);
}

function minutesFor(miles: number): number {
  return Math.max(3, Math.round((miles / AVG_MPH) * 60 + PULL_OUT_MINUTES));
}

/** GET /api/drive-time?address=…&city=…  */
export function simulatedDriveTime(addr: AddressLike, originLabel = ORIGIN_LABEL) {
  const key = addressKey(addr);
  const miles = legMiles(key);
  return {
    ok: true,
    simulated: true,
    demo_note:
      'Drive time is generated from a hash of the address (stable per address) at '
      + `${AVG_MPH} mph plus a ${PULL_OUT_MINUTES}-minute pull-out allowance — no routing service is called.`,
    miles,
    minutes: minutesFor(miles),
    cached: false,
    found: true,
    origin: originLabel,
  };
}

/** POST /api/drive-time — office → each stop in order → office. */
export function simulatedRoundTrip(stops: AddressLike[]) {
  const keys = stops.map(addressKey).filter(Boolean);
  if (!keys.length) {
    return {
      ok: true, simulated: true, miles: 0, minutes: 0, cached: false, found: false,
      origin: ORIGIN_LABEL, reason: 'no_address',
      demo_note: 'No stops were supplied, so there is nothing to route.',
    };
  }
  // Out to the first stop, between each pair, and home from the last.
  let miles = legMiles(keys[0]) + legMiles(keys[keys.length - 1]);
  for (let i = 1; i < keys.length; i++) miles += legMiles(`${keys[i - 1]}→${keys[i]}`) * 0.55;
  miles = round2(miles);
  return {
    ok: true,
    simulated: true,
    demo_note:
      'Round trip is generated from hashes of the stop addresses (office → stops in order → office); '
      + 'no routing service is called.',
    miles,
    minutes: minutesFor(miles) + (keys.length - 1) * 2,
    cached: false,
    found: true,
    origin: ORIGIN_LABEL,
    stops: keys.length,
  };
}
